/**
 * ARChaseTemplatesCard — three tone-tiered reminder bodies (friendly / firm / final).
 * The chase button on an invoice auto-picks the tier based on days overdue:
 *   1-30 → friendly, 31-60 → firm, 61+ → final.
 */
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MessageSquareWarning, Loader2, RotateCcw, CheckCircle2, Eye, X } from "lucide-react";
import { api } from "@/lib/api";

const TIERS = [
  { k: "friendly", label: "Friendly reminder", tone: "amber", rule: "1 – 30 days overdue" },
  { k: "firm",     label: "Second notice",    tone: "orange", rule: "31 – 60 days overdue" },
  { k: "final",    label: "Final notice",     tone: "red", rule: "61+ days overdue" },
];
const TONE_BG = { amber: "#FEF3C7", orange: "#FFEDD5", red: "#FEE2E2" };
const TONE_FG = { amber: "#92400E", orange: "#9A3412", red: "#991B1B" };

export default function ARChaseTemplatesCard() {
  const [data, setData] = useState(null);
  const [draft, setDraft] = useState({ friendly: "", firm: "", final: "" });
  const [saving, setSaving] = useState(null); // tier key currently saving
  const [savedTier, setSavedTier] = useState(null);
  const [savedAt, setSavedAt] = useState(0);
  const [previewFor, setPreviewFor] = useState(null); // {tier, subject, body, sample}
  const [previewing, setPreviewing] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/ar-chase-templates");
        setData(r.data);
        setDraft({ friendly: r.data.friendly, firm: r.data.firm, final: r.data.final });
      } catch { toast.error("Failed to load A/R chase templates"); }
    })();
  }, []);

  const saveTier = async (tier) => {
    setSaving(tier);
    try {
      await api.patch("/tenant", { [`ar_chase_${tier}`]: draft[tier] });
      setSavedTier(tier);
      setSavedAt(Date.now());
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    } finally { setSaving(null); }
  };

  const resetTier = (tier) => {
    if (!window.confirm("Reset this tier to the default template? Your current text will be lost.")) return;
    setDraft((d) => ({ ...d, [tier]: "" }));
  };

  const previewTier = async (tier) => {
    setPreviewing(tier);
    try {
      // Preview whatever's in the editor right now (not just what's saved)
      const r = await api.post("/ar-chase-templates/preview", {
        tier,
        template: draft[tier] || null,
      });
      setPreviewFor(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Preview failed");
    } finally { setPreviewing(null); }
  };

  if (!data) {
    return (
      <div className="border p-6 bg-white" style={{ borderColor: "var(--ll-border)" }}>
        <Loader2 className="animate-spin" size={16} />
      </div>
    );
  }

  return (
    <div className="border bg-white" style={{ borderColor: "var(--ll-border)" }} data-testid="ar-chase-templates-card">
      <div className="px-5 py-3 border-b bg-[#0b5d9e] text-white" style={{ borderColor: "var(--ll-border)" }}>
        <div className="text-[10px] uppercase tracking-widest opacity-75">A/R Chase Playbook</div>
        <div className="font-display font-bold text-sm flex items-center gap-2">
          <MessageSquareWarning size={14} /> Three-tier reminder templates
        </div>
      </div>

      <div className="p-5 space-y-4">
        <p className="text-xs text-[var(--ll-ink-soft)]">
          The <strong>Chase</strong> button on an invoice automatically picks the right tone based on
          how many days the bill is past due. Leave a tier blank to use the built-in default.
        </p>

        <div className="text-[11px] text-[var(--ll-ink-muted)]">
          Available placeholders:{" "}
          {data.placeholders.map((p) => (
            <code key={p} className="mx-0.5 px-1 bg-[var(--ll-bg-card)] rounded">{p}</code>
          ))}
        </div>

        {TIERS.map((tier) => {
          const isSaving = saving === tier.k;
          const justSaved = savedTier === tier.k && (Date.now() - savedAt < 2500);
          const value = draft[tier.k] || "";
          const effective = value || data.defaults[tier.k];
          const usingDefault = !value;
          return (
            <div
              key={tier.k}
              className="border"
              style={{ borderColor: "var(--ll-border)" }}
              data-testid={`ar-tier-${tier.k}`}
            >
              <div
                className="px-3 py-2 flex items-center justify-between"
                style={{ background: TONE_BG[tier.tone], color: TONE_FG[tier.tone] }}
              >
                <div className="text-xs font-bold flex items-center gap-2">
                  {tier.label}
                  <span className="font-normal text-[11px] opacity-80">· {tier.rule}</span>
                  {usingDefault && (
                    <span className="text-[10px] uppercase tracking-widest font-medium opacity-80">(default)</span>
                  )}
                </div>
                <div className="flex items-center gap-2">
                  {justSaved && (
                    <span className="text-[10px] uppercase tracking-widest flex items-center gap-1">
                      <CheckCircle2 size={11} /> Saved
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={() => resetTier(tier.k)}
                    data-testid={`ar-tier-${tier.k}-reset`}
                    disabled={usingDefault}
                    title={usingDefault ? "Already using default" : "Reset to default template"}
                    className="text-[11px] uppercase tracking-widest flex items-center gap-1 disabled:opacity-40 hover:underline"
                  >
                    <RotateCcw size={11} /> Reset
                  </button>
                </div>
              </div>
              <textarea
                rows={6}
                value={effective}
                onChange={(e) => setDraft((d) => ({ ...d, [tier.k]: e.target.value }))}
                className="w-full px-3 py-2 text-sm font-mono-fin"
                style={{ border: "none", outline: "none", background: "white" }}
                data-testid={`ar-tier-${tier.k}-textarea`}
                maxLength={2000}
              />
              <div className="px-3 py-2 border-t flex items-center justify-end gap-2" style={{ borderColor: "var(--ll-border)" }}>
                <button
                  type="button"
                  onClick={() => previewTier(tier.k)}
                  disabled={previewing === tier.k}
                  data-testid={`ar-tier-${tier.k}-preview`}
                  title="Preview this chase email with sample data"
                  className="text-xs px-3 py-1.5 border hover:bg-[var(--ll-mint)] disabled:opacity-50 flex items-center gap-1"
                  style={{ borderColor: "var(--ll-border)" }}
                >
                  {previewing === tier.k ? <Loader2 size={11} className="animate-spin" /> : <Eye size={11} />}
                  Preview this chase email
                </button>
                <button
                  type="button"
                  onClick={() => saveTier(tier.k)}
                  disabled={isSaving}
                  data-testid={`ar-tier-${tier.k}-save`}
                  className="text-xs px-3 py-1.5 bg-[#0b5d9e] text-white hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
                >
                  {isSaving ? <Loader2 size={11} className="animate-spin" /> : <CheckCircle2 size={11} />}
                  Save {tier.label.toLowerCase()}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <ChasePreviewModal preview={previewFor} onClose={() => setPreviewFor(null)} />
    </div>
  );
}


function ChasePreviewModal({ preview, onClose }) {
  if (!preview) return null;
  const toneBg = { friendly: "#FEF3C7", firm: "#FFEDD5", final: "#FEE2E2" };
  const toneFg = { friendly: "#92400E", firm: "#9A3412", final: "#991B1B" };
  return (
    <div
      className="fixed inset-0 z-[90] bg-black/50 grid place-items-center p-4"
      onClick={onClose}
      data-testid="chase-preview-modal"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white border w-full max-w-xl max-h-[92vh] overflow-y-auto shadow-xl"
        style={{ borderColor: "var(--ll-ink)" }}
      >
        <div
          className="px-5 py-3 border-b bg-[#0b5d9e] text-white flex items-center justify-between"
          style={{ borderColor: "var(--ll-border)" }}
        >
          <div>
            <div className="text-[10px] uppercase tracking-widest opacity-75">Preview with sample data</div>
            <div className="font-display font-bold text-sm">{preview.tier_label}</div>
          </div>
          <button type="button" onClick={onClose} className="text-white/80 hover:text-white" data-testid="chase-preview-close">
            <X size={16} />
          </button>
        </div>
        <div className="p-5 space-y-4 text-sm">
          <div
            className="px-3 py-2 text-xs"
            style={{ background: toneBg[preview.tier], color: toneFg[preview.tier] }}
          >
            This is how a <strong>{preview.tier_label}</strong> email will look to the client. No email is actually sent.
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Subject</div>
            <div className="border px-3 py-2 bg-[var(--ll-bg-card)] text-sm" style={{ borderColor: "var(--ll-border)" }} data-testid="chase-preview-subject">
              {preview.subject}
            </div>
          </div>

          <div>
            <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1">Body</div>
            <pre
              className="border px-3 py-3 bg-white text-sm whitespace-pre-wrap font-mono-fin"
              style={{ borderColor: "var(--ll-border)", fontFamily: "Georgia, serif", lineHeight: 1.5 }}
              data-testid="chase-preview-body"
            >{preview.body}</pre>
          </div>

          <div className="border p-3" style={{ borderColor: "var(--ll-border)", background: "var(--ll-bg-card)" }}>
            <div className="text-[10px] uppercase tracking-widest text-[var(--ll-ink-muted)] mb-1.5">
              Placeholder values used in this preview
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
              {Object.entries(preview.sample).map(([k, v]) => (
                <div key={k} className="flex gap-2">
                  <code className="font-bold">{"{" + k + "}"}</code>
                  <span className="truncate">{String(v)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
        <div className="sticky bottom-0 bg-white border-t px-5 py-3 flex items-center justify-end" style={{ borderColor: "var(--ll-border)" }}>
          <button
            type="button"
            onClick={onClose}
            data-testid="chase-preview-done"
            className="text-sm px-3 py-2 bg-[#0b5d9e] text-white hover:opacity-90"
          >
            Looks good
          </button>
        </div>
      </div>
    </div>
  );
}
